import { ISignRequest } from "@modals/api.modal";

export interface IUserDetails {
  CustomerId: string;
  CustomerName: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Mobile: string;
}

export enum EnumRequestStatus {
  ALL = "All",
  OPEN = "Open",
  SIGNED = "Signed",
  PARTIAL_SIGNED = "PartialSigned",
  DECLINED = "Declined",
  CANCELLED = "Cancelled",
}

export type RequestStatus = keyof typeof EnumRequestStatus;

export interface IRequestStatus {
  status: string;
  count: number;
  label: string;
}

export interface IExpandedRowsData {
  data: Array<any>;
  columns: Array<any>;
  actions?: Array<ITableActionRow>
}

export type TSignRequestTableRow = ISignRequest & {
  isDownloading?: boolean;
  isDownloadingAudit?: boolean;
  isRecalling?: boolean;
  expandedRowsData?: IExpandedRowsData;
};

export interface ITableActionRow {
  icon: string;
  imgIcon?: string;
  color: string;
  title: string;
  tooltipPosition?: "above" | "below" | "left" | "right";
  getTooltip?: (row: any) => string;
  isLoading?: (row: any) => boolean;
  isDisabled?: (row: any) => boolean;
  actionFn: (row: any) => void;
}

export enum EnumSignTagTypes {
  SIGNATURE = "Esign",
  INITIALS = "Initials",
  STAMP = "Stamp",
  DATE = "Date",
  CHECKBOX = "Checkbox",
  COMMENT = "Comment",
  TEXT = "Text"
}

export interface ISignTag {
  Height: number;
  Page: number;
  Text: string;
  Type: EnumSignTagTypes;
  Width: number;
  XLocation: number;
  YLocation: number;
  IsRendered?: boolean;
}

export interface IUploadPDFUser {
  name: string;
  email: string;
  emailCc?: string;
  phone: string;
  role: string;
  
  allowedActions?: string;
  order?: number;
  id?: string;
  tempID: string;
}

export const SIGNER_ALLOWED_ACTIONS_EXCLUSIVE = ["All", "TaggedSignatureOnly"] as const;

export const SIGNER_ALLOWED_ACTIONS_TAGS = [
  "SIGNATURE",
  "COMMENT",
  "STAMP",
  "CHECKBOX",
  "DATE",
  "INITIALS",
  "TEXT",
] as const;


export const SIGNER_ALLOWED_ACTIONS_OPTIONS = [
  ...SIGNER_ALLOWED_ACTIONS_EXCLUSIVE,
  ...SIGNER_ALLOWED_ACTIONS_TAGS,
] as const;

export const SIGNER_ALLOWED_ACTIONS_OPTION_LABELS: Record<string, string> = {
  All: "allowedActionsAll",
  TaggedSignatureOnly: "allowedActionsTaggedSignatureOnly",
  SIGNATURE: "allowedActionsSignature",
  COMMENT: "allowedActionsComment",
  STAMP: "allowedActionsStamp",
  CHECKBOX: "allowedActionsCheckbox",
  DATE: "allowedActionsDate",
  INITIALS: "allowedActionsInitials",
  TEXT: "allowedActionsText",
};

export const DEFAULT_SIGNER_ALLOWED_ACTIONS = "All";

export const REVIEWER_ALLOWED_ACTIONS_OPTIONS = ["COMMENT"] as const;

export const REVIEWER_DEFAULT_ALLOWED_ACTIONS = "COMMENT";

/** Allowed Actions dropdown options for a manage-users row role. */
export function getAllowedActionsDropdownOptionsForRole(
  role: string | undefined | null
): readonly string[] {
  if ((role ?? "").toString().trim().toLowerCase() === "reviewer") {
    return REVIEWER_ALLOWED_ACTIONS_OPTIONS;
  }
  return SIGNER_ALLOWED_ACTIONS_OPTIONS;
}

/** Stored allowed-actions value for a row role (reviewer is always Comment-only). */
export function normalizeAllowedActionsForRole(
  role: string | undefined | null,
  allowedActions?: string | null
): string {
  if ((role ?? "").toString().trim().toLowerCase() === "reviewer") {
    return REVIEWER_DEFAULT_ALLOWED_ACTIONS;
  }
  return normalizeSignerAllowedActions(allowedActions ?? DEFAULT_SIGNER_ALLOWED_ACTIONS);
}

export const SIGNER_ALLOWED_ACTIONS_EXCEL_LABELS: Record<
  (typeof SIGNER_ALLOWED_ACTIONS_OPTIONS)[number],
  string
> = {
  All: "All",
  TaggedSignatureOnly: "Tagged signature only",
  SIGNATURE: "Signature",
  COMMENT: "Comment",
  STAMP: "Stamp",
  CHECKBOX: "Checkbox",
  DATE: "Date",
  INITIALS: "Initials",
  TEXT: "Text",
};

export const SIGNER_ALLOWED_ACTIONS_EXCEL_DROPDOWN = SIGNER_ALLOWED_ACTIONS_OPTIONS.map(
  (key) => SIGNER_ALLOWED_ACTIONS_EXCEL_LABELS[key],
);

const ALLOWED_ACTIONS_TAG_SET = new Set<string>(SIGNER_ALLOWED_ACTIONS_TAGS);
const ALLOWED_ACTIONS_OPTION_SET = new Set<string>(SIGNER_ALLOWED_ACTIONS_OPTIONS);

function mapAllowedActionImportToken(token: string): string {
  const trimmed = token.trim();
  if (!trimmed) return "";
  if (ALLOWED_ACTIONS_OPTION_SET.has(trimmed)) return trimmed;

  const lower = trimmed.toLowerCase();
  for (const key of SIGNER_ALLOWED_ACTIONS_OPTIONS) {
    if (SIGNER_ALLOWED_ACTIONS_EXCEL_LABELS[key].toLowerCase() === lower) return key;
    if (key.toLowerCase() === lower) return key;
  }

  return trimmed;
}

/** Map Excel template allowed-actions cell (label or API value) to stored signer value. */
export function resolveSignerAllowedActionsFromImport(value: string | undefined | null): string {
  const raw = (value ?? "").trim();
  if (!raw) return DEFAULT_SIGNER_ALLOWED_ACTIONS;

  if (!raw.includes(",")) {
    return normalizeSignerAllowedActions(mapAllowedActionImportToken(raw));
  }

  const tokens = raw.split(",").map(mapAllowedActionImportToken).filter(Boolean);
  return formatSignerAllowedActions(tokens);
}

/** Normalize bulk Excel/UI role to signer | reviewer | observer. */
export function normalizeBulkSignerRole(value: string | undefined | null): string {
  const raw = (value ?? "").trim().toLowerCase();
  if (raw === "signer" || raw === "reviewer" || raw === "observer") return raw;
  return "signer";
}

/** Parse API / stored string into selected dropdown values. */
export function parseSignerAllowedActions(value: string | undefined | null): string[] {
  const raw = (value ?? "").trim();
  if (!raw || raw === "All") return ["All"];
  if (raw === "TaggedSignatureOnly") return ["TaggedSignatureOnly"];

  const tags = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => ALLOWED_ACTIONS_TAG_SET.has(part));

  if (
    tags.length === SIGNER_ALLOWED_ACTIONS_TAGS.length &&
    SIGNER_ALLOWED_ACTIONS_TAGS.every((tag) => tags.includes(tag))
  ) {
    return ["All"];
  }

  return tags.length ? tags : ["All"];
}

export function formatSignerAllowedActions(selected: readonly string[]): string {
  const clean = selected.filter((item) => ALLOWED_ACTIONS_OPTION_SET.has(item));
  if (!clean.length || clean.includes("All")) return "All";
  if (clean.includes("TaggedSignatureOnly")) return "TaggedSignatureOnly";

  return SIGNER_ALLOWED_ACTIONS_TAGS.filter((tag) => clean.includes(tag)).join(",");
}


export function normalizeSignerAllowedActions(value: string | undefined | null): string {
  return formatSignerAllowedActions(parseSignerAllowedActions(value));
}


export function mapSignerAllowedActionsToApi(value: string | undefined | null): string {
  const normalized = normalizeSignerAllowedActions(value);
  if (normalized === "All") {
    return [...SIGNER_ALLOWED_ACTIONS_TAGS].join(",");
  }
  return normalized;
}

export type TFieldToolId =
  | "esign"
  | "initials"
  | "stamp"
  | "text"
  | "date"
  | "checkbox"
  | "comment";

export const FIELD_TOOL_TO_ALLOWED_ACTION_TAG: Record<TFieldToolId, string> = {
  esign: "SIGNATURE",
  initials: "INITIALS",
  stamp: "STAMP",
  text: "TEXT",
  date: "DATE",
  checkbox: "CHECKBOX",
  comment: "COMMENT",
};

export interface ISignerAllowedActionsSource {
  role?: string;
  allowedActions?: string;
  AllowedActions?: string;
}

export const FIELD_EDITOR_TOOLS: ReadonlyArray<{
  id: TFieldToolId;
  label: string;
  icon: string;
  requiresSigner: boolean;
}> = [
  { id: "esign", label: "Signature", icon: "draw", requiresSigner: true },
  { id: "initials", label: "Initials", icon: "short_text", requiresSigner: true },
  { id: "stamp", label: "Stamp", icon: "image", requiresSigner: true },
  { id: "text", label: "Text", icon: "text_fields", requiresSigner: true },
  { id: "date", label: "Date", icon: "calendar_today", requiresSigner: true },
  { id: "checkbox", label: "Checkbox", icon: "check_box", requiresSigner: true },
  { id: "comment", label: "Comment", icon: "comment", requiresSigner: false },
];

export function resolveSignerAllowedActionsValue(
  signer: ISignerAllowedActionsSource | null | undefined
): string | undefined {
  if (!signer) return undefined;
  return (signer.allowedActions ?? signer.AllowedActions)?.toString();
}

/** Union of allowed action tags across signer + reviewer rows for tagging tools. */
export function getEnabledAllowedActionTagsForSigners(
  signers: ReadonlyArray<ISignerAllowedActionsSource> | null | undefined
): Set<string> {
  const enabled = new Set<string>();
  const allRows = signers ?? [];
  const signerRows = allRows.filter(
    (row) => (row.role ?? "").toString().trim().toLowerCase() === "signer"
  );
  const reviewerRows = allRows.filter(
    (row) => (row.role ?? "").toString().trim().toLowerCase() === "reviewer"
  );

  if (!signerRows.length && !reviewerRows.length) {
    SIGNER_ALLOWED_ACTIONS_TAGS.forEach((tag) => enabled.add(tag));
    return enabled;
  }

  for (const signer of signerRows) {
    const parsed = parseSignerAllowedActions(resolveSignerAllowedActionsValue(signer));
    if (parsed.includes("All")) {
      SIGNER_ALLOWED_ACTIONS_TAGS.forEach((tag) => enabled.add(tag));
      continue;
    }
    if (parsed.includes("TaggedSignatureOnly")) {
      enabled.add("SIGNATURE");
      continue;
    }
    parsed.forEach((tag) => {
      if (ALLOWED_ACTIONS_TAG_SET.has(tag)) {
        enabled.add(tag);
      }
    });
  }

  for (const reviewer of reviewerRows) {
    const parsed = parseSignerAllowedActions(
      normalizeAllowedActionsForRole("reviewer", resolveSignerAllowedActionsValue(reviewer))
    );
    parsed.forEach((tag) => {
      if (ALLOWED_ACTIONS_TAG_SET.has(tag)) {
        enabled.add(tag);
      }
    });
    enabled.add("COMMENT");
  }

  return enabled;
}

export function isFieldToolAllowedByActions(
  toolId: TFieldToolId,
  signers: ReadonlyArray<ISignerAllowedActionsSource> | null | undefined
): boolean {
  const tag = FIELD_TOOL_TO_ALLOWED_ACTION_TAG[toolId];
  if (!tag) {
    return true;
  }
  return getEnabledAllowedActionTagsForSigners(signers).has(tag);
}

export enum EnumTableDynamicInputType {
  TEXT = "text",
  DROPDOWN = "dropdown",
  MULTI_DROPDOWN = "multi-dropdown",
  TEL = "tel",
  EMAIL = "email",
}

export enum EnumSignerRoleType {
  SIGNER = "Signer",
  REVIEWER = "Reviewer",
  OBSERVER = "Observer"
}

export const BULK_SIGNER_TEMPLATE_HEADERS = [
  "Name",
  "Email",
  "Email CC",
  "Phone",
  "Allowed Actions",
] as const;
